// 📍 B394 (AI_158) — ENDEREÇO DO PACIENTE: FONTE ÚNICA
// ────────────────────────────────────────────────────────────
// O cadastro guarda o endereço em DOIS lugares: o bloco `endereco`
// ({rua, numero, complemento, bairro, cidade, estado, cep}) e os mesmos
// dados repetidos como campos SOLTOS (logradouro, numero, bairro, cidade,
// estado, cep), criados "por compatibilidade".
//
// A tela de edição (VisualizacaoPaciente) só escreve o BLOCO; ao salvar,
// manda o paciente inteiro e os campos soltos voltavam com o valor ANTIGO e
// eram regravados. A leitura (mapearDocPaciente) e TODOS os documentos
// impressos (receituário, pedido, RCE, APAC, GAL, cargas virais) leem os
// campos soltos → endereço corrigido na tela, receita saindo com o antigo.
// Mesma classe do B345 (telefone × telefone1).
//
// Regra: o BLOCO é o que os formulários escrevem → é a fonte da verdade.
//   • leitura: bloco primeiro, campos soltos só como reserva (legado);
//   • gravação: espelhar o bloco nos campos soltos (cada paciente se conserta
//     na primeira edição, sem migração).
// ────────────────────────────────────────────────────────────

export interface EnderecoBloco {
  rua?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  cep?: string | null;
}

export interface EnderecoPlano {
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
}

const limpo = (v: unknown): string | undefined => {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  return t ? t : undefined;
};

/** O bloco tem pelo menos um campo preenchido? */
export function blocoTemEndereco(bloco: EnderecoBloco | null | undefined): boolean {
  if (!bloco || typeof bloco !== "object") return false;
  return Boolean(
    limpo(bloco.rua) ||
      limpo(bloco.numero) ||
      limpo(bloco.complemento) ||
      limpo(bloco.bairro) ||
      limpo(bloco.cidade) ||
      limpo(bloco.estado) ||
      limpo(bloco.cep)
  );
}

/**
 * Campos soltos derivados do BLOCO (para gravar/espelhar).
 * Só faz sentido quando o bloco tem conteúdo; caller decide.
 */
export function planoDoBloco(bloco: EnderecoBloco): EnderecoPlano {
  return {
    logradouro: limpo(bloco.rua),
    numero: limpo(bloco.numero),
    complemento: limpo(bloco.complemento),
    bairro: limpo(bloco.bairro),
    cidade: limpo(bloco.cidade),
    estado: limpo(bloco.estado),
    cep: limpo(bloco.cep),
  };
}

/**
 * Endereço VIGENTE para leitura: o bloco manda; os campos soltos só entram
 * quando o bloco está vazio (paciente legado que nunca teve bloco).
 *
 * Campo a campo NÃO mistura: se o bloco tem conteúdo, um campo vazio no bloco
 * fica vazio (senão o bairro antigo "vazaria" para um endereço novo sem bairro).
 */
export function enderecoVigente(
  data: (EnderecoPlano & { endereco?: EnderecoBloco | null }) | null | undefined
): EnderecoPlano {
  if (!data) return {};
  if (blocoTemEndereco(data.endereco)) {
    return planoDoBloco(data.endereco as EnderecoBloco);
  }
  return {
    logradouro: limpo(data.logradouro),
    numero: limpo(data.numero),
    complemento: limpo(data.complemento),
    bairro: limpo(data.bairro),
    cidade: limpo(data.cidade),
    estado: limpo(data.estado),
    cep: limpo(data.cep),
  };
}
